import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, Platform,
} from 'react-native';
import { useLocalSearchParams, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { OtaUpdateNowCard } from '../../../src/components/admin/OtaPolicy';
import ControllerEquipmentPanel from '../../../src/components/controller/ControllerEquipmentPanel';
import { api, apiPost } from '../../../src/api/client';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import PageFooter from '../../../src/components/PageFooter';

interface ControllerDetail {
  id: string;
  ownerId: string | null;
  firmwareVersion: string | null;
  isOnline: boolean;
  wifiRssi: number | null;
  ipAddress: string | null;
  uptimeSeconds: number | null;
  freeHeap: number | null;
  lastSeen: string | null;
  relayStates: number[] | null;
  relayCycles: number[] | null;
  /** 1.4 P04 */
  model?: string;
  modelLabel?: string;
  relayCount?: number;
  inputCount?: number;
  firmwareFamily?: string | null;
  capabilities?: string[];
  reportedModel?: string | null;
  bayId: string | null;
  bayName: string | null;
  locationName: string | null;
  ownerBusinessName: string | null;
  claimedAt: string | null;
  otaStatus: string | null;
  otaVersion: string | null;
  createdAt: string;
}

interface FwVersion {
  id: string;
  version: string;
  releaseNotes: string | null;
  createdAt: string;
}

interface LogEntry {
  id: string;
  direction: string;
  topic: string;
  payload: any;
  createdAt: string;
}

/** AdminControllerDetail - telemetry, relay states, firmware OTA, and MQTT logs for a controller. */
export default function AdminControllerDetail() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [ctrl, setCtrl] = useState<ControllerDetail | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [fwVersions, setFwVersions] = useState<FwVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [cmdLoading, setCmdLoading] = useState('');
  const [deployingFirmware, setDeployingFirmware] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [mqttCreds, setMqttCreds] = useState<{ username: string; password: string } | null>(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [credsVisible, setCredsVisible] = useState(false);

  const fetchData = async (signal?: AbortSignal) => {
    try {
      const [ctrlData, logsData, fwData] = await Promise.all([
        api<ControllerDetail>(`/api/admin/controllers/${id}`),
        api<{ logs: LogEntry[] }>(`/api/admin/controllers/${id}/logs?limit=20`),
        api<FwVersion[]>('/api/admin/firmware').catch(() => [] as FwVersion[]),
      ]);
      if (signal?.aborted) return;
      setCtrl(ctrlData);
      setLogs(logsData.logs || []);
      setFwVersions(Array.isArray(fwData) ? fwData : []);
    } catch (err: any) {
      if (!(signal?.aborted)) console.error(err);
    } finally {
      if (!(signal?.aborted)) setLoading(false);
    }
  };

  useEffect(() => { const ac = new AbortController(); fetchData(ac.signal); return () => ac.abort(); }, [id]);

  const fetchMqttCreds = async () => {
    setCredsLoading(true);
    try {
      const data = await api<{ username: string; password: string }>(`/api/admin/controllers/${id}/mqtt-credentials`);
      setMqttCreds(data);
      setCredsVisible(true);
    } catch (err: any) {
      alert(err.message || 'Failed to load MQTT credentials');
    } finally {
      setCredsLoading(false);
    }
  };

  const sendCommand = async (command: string, extra?: any) => {
    setCmdLoading(command);
    try {
      const result = await apiPost<{ success: boolean; error?: string }>(`/api/admin/controllers/${id}/command`, { command, ...extra });
      const msg = result.success ? `${command} executed successfully` : `${command} failed: ${result.error}`;
      alert(msg);
      fetchData();
    } catch (err: any) {
      alert(err.message || 'Command failed');
    } finally {
      setCmdLoading('');
    }
  };

  const confirmRestart = () => {
    if (confirm('Are you sure you want to restart this controller?')) {
      sendCommand('restart');
    }
  };

  const handleDeployFirmware = (version: string) => {
    if (!confirm(`Deploy firmware v${version} to this controller?`)) return;
    setDeployingFirmware(true);
    apiPost('/api/admin/firmware/deploy', { version, controllerIds: [id] })
      .then(() => {
        alert(`Firmware ${version} deployment initiated`);
        fetchData();
      })
      .catch((err: any) => alert(err.message || 'Failed to deploy firmware'))
      .finally(() => setDeployingFirmware(false));
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 80 }} />
      </View>
    );
  }

  if (!ctrl) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>Controller not found</Text>
      </View>
    );
  }

  const relayCount = ctrl.relayCount || 4;
  const relayIdx = Array.from({ length: relayCount }, (_, i) => i);
  const relayStates = relayIdx.map(i => ctrl.relayStates?.[i] ?? 0);
  const relayCycles = relayIdx.map(i => ctrl.relayCycles?.[i] ?? 0);

  return (
    <>
      <Stack.Screen options={{ title: ctrl.id }} />
      <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
        {/* Status Header */}
        <View style={styles.statusCard}>
          <View style={styles.statusHeader}>
            <View style={[styles.statusDot, { backgroundColor: ctrl.isOnline ? '#059669' : '#dc2626' }]} />
            <Text style={styles.statusText}>{ctrl.isOnline ? 'Online' : 'Offline'}</Text>
            {ctrl.lastSeen && (
              <Text style={styles.lastSeen}>Last seen: {new Date(ctrl.lastSeen).toLocaleString()}</Text>
            )}
          </View>
        </View>

        {/* Telemetry */}
        <Text style={styles.sectionTitle}>Telemetry</Text>
        <View style={styles.card}>
          <InfoRow label="Model" value={`${ctrl.modelLabel || ctrl.model || 'RCP-Alpha-PoE'} (${ctrl.model || 'DTW-4R'}) · ${relayCount} relays · ${ctrl.inputCount ?? 0} inputs`} colors={colors} />
          {!!ctrl.reportedModel && (
            <InfoRow label="Reported model" value={`${ctrl.reportedModel} — MISMATCH, OTA blocked until the row is corrected`} colors={colors} />
          )}
          <InfoRow label="Firmware" value={`${ctrl.firmwareVersion || 'Unknown'}${ctrl.firmwareFamily ? ` (${ctrl.firmwareFamily})` : ''}`} colors={colors} />
          {!!ctrl.capabilities?.length && (
            <InfoRow label="Capabilities" value={ctrl.capabilities.join(', ')} colors={colors} />
          )}
          <InfoRow label="IP Address" value={ctrl.ipAddress || 'N/A'} colors={colors} />
          <InfoRow label="WiFi RSSI" value={ctrl.wifiRssi != null ? `${ctrl.wifiRssi} dBm` : 'N/A'} colors={colors} />
          <InfoRow label="Uptime" value={ctrl.uptimeSeconds != null ? formatUptime(ctrl.uptimeSeconds) : 'N/A'} colors={colors} />
          <InfoRow label="Free Heap" value={ctrl.freeHeap != null ? `${Math.round(ctrl.freeHeap / 1024)} KB` : 'N/A'} colors={colors} />
        </View>

        {/* MQTT Provisioning */}
        <Text style={styles.sectionTitle}>MQTT Provisioning</Text>
        <View style={styles.card}>
          <InfoRow label="Device ID" value={ctrl.id} colors={colors} mono />
          <InfoRow label="MQTT Username" value={ctrl.id} colors={colors} mono />
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 }}>
            <Text style={{ fontSize: 14, color: colors.textSecondary }}>MQTT Password</Text>
            {credsVisible && mqttCreds ? (
              <Text style={{ fontSize: 11, color: colors.text, fontWeight: '500', fontFamily: 'monospace', flexShrink: 1, textAlign: 'right' }} selectable>
                {mqttCreds.password}
              </Text>
            ) : (
              <TouchableOpacity
                onPress={fetchMqttCreds}
                disabled={credsLoading}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
              >
                {credsLoading ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <>
                    <Ionicons name="eye-outline" size={14} color={colors.primary} />
                    <Text style={{ fontSize: 12, color: colors.primary, fontWeight: '600' }}>Show</Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* 1.4 P19: relays with action labels, inputs, buzzer, events (owner routes scoped by ?ownerId=) */}
        <ControllerEquipmentPanel ctrl={ctrl as any} client={{ get: (p) => api(`${p}${p.includes('?') ? '&' : '?'}ownerId=${ctrl.ownerId ?? ''}`), put: (p, b) => api(`${p}?ownerId=${ctrl.ownerId ?? ''}`, { method: 'PUT', body: b }) }} onLive={(s) => setCtrl(c => (c && c.id === s.id && (c.isOnline !== s.isOnline || c.lastSeen !== s.lastSeen)) ? { ...c, isOnline: s.isOnline, lastSeen: s.lastSeen } : c)} onSettingsChanged={() => fetchData()} />

        {/* 1.4 P21: policy-driven update now */}
        <OtaUpdateNowCard controllerId={ctrl.id} firmwareVersion={ctrl.firmwareVersion} otaStatus={ctrl.otaStatus} onDone={() => fetchData()} />

        {/* Ownership & Assignment */}
        <Text style={styles.sectionTitle}>Ownership & Assignment</Text>
        <View style={styles.card}>
          <InfoRow label="Owner" value={ctrl.ownerBusinessName || (ctrl.ownerId ? ctrl.ownerId.substring(0, 8) + '...' : 'Unclaimed')} colors={colors} />
          {ctrl.claimedAt && <InfoRow label="Claimed" value={new Date(ctrl.claimedAt).toLocaleDateString()} colors={colors} />}
          <InfoRow label="Location" value={ctrl.locationName || 'Unassigned'} colors={colors} />
          <InfoRow label="Bay" value={ctrl.bayName || 'Unassigned'} colors={colors} />
          <InfoRow label="Created" value={new Date(ctrl.createdAt).toLocaleDateString()} colors={colors} />
        </View>

        {/* Firmware */}
        <Text style={styles.sectionTitle}>Firmware</Text>
        <View style={styles.card}>
          <InfoRow label="Current Version" value={ctrl.firmwareVersion || 'Unknown'} colors={colors} />
          {ctrl.otaStatus === 'pending' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6 }}>
              <Ionicons name="cloud-download" size={16} color="#d97706" />
              <Text style={{ fontSize: 14, color: '#d97706', fontWeight: '500' }}>
                Deploying v{ctrl.otaVersion}...
              </Text>
            </View>
          )}
          {ctrl.otaStatus === 'completed' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6 }}>
              <Ionicons name="checkmark-circle" size={16} color="#059669" />
              <Text style={{ fontSize: 14, color: '#059669', fontWeight: '500' }}>
                OTA v{ctrl.otaVersion} completed
              </Text>
            </View>
          )}
          {fwVersions.length > 0 && (
            <View style={{ marginTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.borderLight, paddingTop: spacing.sm }}>
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Available Versions</Text>
              {fwVersions.map((fw: any) => {
                const isCurrent = ctrl.firmwareVersion === fw.version;
                const isPending = ctrl.otaStatus === 'pending' && ctrl.otaVersion === fw.version;
                return (
                  <View key={fw.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6 }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 14, color: colors.text, fontWeight: '500' }}>
                        v{fw.version}
                        {isCurrent ? ' (current)' : ''}
                        {isPending ? ' (deploying...)' : ''}
                        {fw.isStable ? '' : ' [unstable]'}
                      </Text>
                      {fw.releaseNotes && (
                        <Text style={{ fontSize: 12, color: colors.textSecondary }} numberOfLines={1}>{fw.releaseNotes}</Text>
                      )}
                    </View>
                    {!isCurrent && !isPending && (
                      <TouchableOpacity
                        style={{
                          paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6,
                          backgroundColor: colors.primary + '15', borderWidth: 1, borderColor: colors.primary + '30',
                        }}
                        onPress={() => handleDeployFirmware(fw.version)}
                        disabled={deployingFirmware}
                      >
                        <Text style={{ fontSize: 12, color: colors.primary, fontWeight: '600' }}>
                          {deployingFirmware ? '...' : 'Deploy'}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </View>

        {/* Commands */}
        <Text style={styles.sectionTitle}>Commands</Text>
        <View style={styles.commandGrid}>
          <CommandBtn
            icon="flash" label="Test Relays" color="#059669"
            loading={cmdLoading === 'relay_test'}
            onPress={() => sendCommand('relay_test')}
          />
          <CommandBtn
            icon="pulse" label="Request Status" color={colors.primary}
            loading={cmdLoading === 'status'}
            onPress={() => sendCommand('status')}
          />
          <CommandBtn
            icon="refresh" label="Restart" color="#dc2626"
            loading={cmdLoading === 'restart'}
            onPress={confirmRestart}
          />
        </View>

        {/* Test Single Relay */}
        <Text style={styles.sectionTitle}>Test Single Relay</Text>
        <View style={styles.relayTestGrid}>
          {relayIdx.map(i => i + 1).map(relay => (
            <TouchableOpacity
              key={relay}
              style={styles.relayTestBtn}
              onPress={() => sendCommand('test_relay', { relay, mode: 'pulse' })}
              disabled={!!cmdLoading}
            >
              {cmdLoading === `test_relay` ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <>
                  <Ionicons name="flash-outline" size={18} color={colors.primary} />
                  <Text style={styles.relayTestLabel}>Relay {relay}</Text>
                </>
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* Recent Logs */}
        <Text style={styles.sectionTitle}>Recent MQTT Activity</Text>
        {logs.length === 0 ? (
          <Text style={styles.noLogs}>No recent activity</Text>
        ) : (
          <View style={styles.logsCard}>
            {logs.map((log) => {
              const isHeartbeat = log.topic?.includes('/status/heartbeat');
              const displayTopic = log.topic?.replace(/^rp\/[^/]+\//, 'rp/.../') || log.topic;
              return (
              <TouchableOpacity
                key={log.id}
                activeOpacity={0.7}
                onPress={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
              >
                <View style={styles.logRow}>
                  <View style={styles.logLeft}>
                    <Ionicons
                      name={isHeartbeat ? 'heart' : (log.direction === 'inbound' ? 'arrow-down' : 'arrow-up')}
                      size={14}
                      color={isHeartbeat ? '#ec4899' : (log.direction === 'inbound' ? '#059669' : '#3b82f6')}
                    />
                    <Text style={styles.logTopic} numberOfLines={expandedLogId === log.id ? undefined : 1}>{displayTopic}</Text>
                  </View>
                  <Text style={styles.logTime}>
                    {new Date(log.createdAt).toLocaleTimeString()}
                  </Text>
                </View>
                {expandedLogId === log.id && log.payload && (
                  <View style={styles.logPayload}>
                    <Text style={styles.logPayloadText}>
                      {typeof log.payload === 'string' ? log.payload : JSON.stringify(log.payload, null, 2)}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            );
            })}
          </View>
        )}
        <PageFooter />
      </ScrollView>
    </>
  );
}

function InfoRow({ label, value, colors, mono }: { label: string; value: string; colors: any; mono?: boolean }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 }}>
      <Text style={{ fontSize: 14, color: colors.textSecondary }}>{label}</Text>
      <Text style={{ fontSize: mono ? 11 : 14, color: colors.text, fontWeight: '500', fontFamily: mono ? 'monospace' : undefined, flexShrink: 1, textAlign: 'right' }} selectable={mono} numberOfLines={mono ? undefined : 1}>
        {value}
      </Text>
    </View>
  );
}

function CommandBtn({ icon, label, color, loading, onPress }: {
  icon: string; label: string; color: string; loading: boolean; onPress: () => void;
}) {
  return (
    <TouchableOpacity
      style={{
        flex: 1, alignItems: 'center', paddingVertical: 14, borderRadius: 10,
        backgroundColor: color + '15', borderWidth: 1, borderColor: color + '30',
      }}
      onPress={onPress}
      disabled={loading}
    >
      {loading ? (
        <ActivityIndicator size="small" color={color} />
      ) : (
        <>
          <Ionicons name={icon as keyof typeof Ionicons.glyphMap} size={22} color={color} />
          <Text style={{ fontSize: 12, color, fontWeight: '600', marginTop: 4 }}>{label}</Text>
        </>
      )}
    </TouchableOpacity>
  );
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  errorText: { fontSize: fontSize.md, color: c.textSecondary },
  statusCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md, borderWidth: 1, borderColor: c.borderLight,
  },
  statusHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  statusDot: { width: 12, height: 12, borderRadius: 6 },
  statusText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  lastSeen: { fontSize: fontSize.xs, color: c.textSecondary, marginLeft: 'auto' },
  sectionTitle: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary,
    textTransform: 'uppercase', letterSpacing: 1, marginBottom: spacing.sm, marginTop: spacing.md,
  },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    borderWidth: 1, borderColor: c.borderLight,
  },
  relayGrid: { flexDirection: 'row', gap: spacing.sm },
  relayCard: {
    flex: 1, alignItems: 'center', paddingVertical: spacing.md, borderRadius: borderRadius.md,
    borderWidth: 1,
  },
  relayOff: { backgroundColor: c.surface, borderColor: c.borderLight },
  relayOn: { backgroundColor: '#dc262615', borderColor: '#dc2626' },
  relayLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.textSecondary },
  relayLabelOn: { color: '#dc2626' },
  relayState: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  relayStateOn: { color: '#dc2626', fontWeight: fontWeight.bold },
  relayCycles: { fontSize: 10, color: c.textLight, marginTop: 2 },
  commandGrid: { flexDirection: 'row', gap: spacing.sm },
  relayTestGrid: { flexDirection: 'row', gap: spacing.sm },
  relayTestBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 4, paddingVertical: 10, borderRadius: borderRadius.sm,
    backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight,
  },
  relayTestLabel: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium },
  logsCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight,
    overflow: 'hidden',
  },
  logRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 8, paddingHorizontal: spacing.md,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  logLeft: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 },
  logTopic: { fontSize: fontSize.xs, color: c.text, fontFamily: 'monospace', flex: 1 },
  logTime: { fontSize: fontSize.xs, color: c.textLight },
  noLogs: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', paddingVertical: spacing.lg },
  logPayload: {
    backgroundColor: c.background, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  logPayloadText: {
    fontSize: 11, fontFamily: 'monospace', color: c.text, lineHeight: 16,
  },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
